import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clientsApi, contactsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Mail, Phone, MapPin, Cpu, Radio, ArrowUpRight } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const ADDR_FIELDS = [
  { k: "address_line1", label: "Address line 1" },
  { k: "address_line2", label: "Address line 2" },
  { k: "zipcode", label: "Zip code" },
  { k: "city", label: "City" },
  { k: "country", label: "Country" },
];

function ContactCombobox({ contacts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = contacts.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="client-edit-contact"
          className="mt-1.5 w-full flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white hover:bg-zinc-50"
        >
          <span className={cn(!selected && "text-zinc-400")}>
            {selected ? <>
              <span className="font-medium">{selected.name}</span>
              <span className="text-xs text-zinc-500 ml-2 font-mono">{selected.email}</span>
            </> : "Select a contact…"}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-zinc-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search contacts…" />
          <CommandList>
            <CommandEmpty>No contacts found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }}>
                <Check className={cn("w-3.5 h-3.5 mr-2", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-zinc-500 italic">No primary contact</span>
              </CommandItem>
              {contacts.map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.email}`} onSelect={() => { onChange(c.id); setOpen(false); }}>
                  <Check className={cn("w-3.5 h-3.5 mr-2", value === c.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-xs text-zinc-500 font-mono">{c.email}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function EditClientModal({ client, contacts, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...client });
  const updateM = useMutation({
    mutationFn: ({ id, data }) => clientsApi.update(id, data),
    onSuccess: () => {
      toast.success("Client updated");
      qc.invalidateQueries({ queryKey: ["clients"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold mb-3">Edit client</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.name?.trim()) return toast.error("Name is required");
            updateM.mutate({ id: client.id, data: form });
          }}
          className="space-y-3"
          data-testid="client-edit-form"
        >
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Name</span>
            <input
              data-testid="client-edit-name"
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>

          <div className="border-t border-zinc-100 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Address</div>
            {ADDR_FIELDS.map(({ k, label }) => (
              <label key={k} className="block mb-2">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
                <input
                  data-testid={`client-edit-${k}`}
                  className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </label>
            ))}
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Primary contact</span>
              <ContactCombobox
                contacts={contacts}
                value={form.primary_contact_id}
                onChange={(id) => setForm({ ...form, primary_contact_id: id })}
              />
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">
              Cancel
            </button>
            <button data-testid="client-edit-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const client = clients.data?.find((c) => c.id === id);

  const devices = useQuery({ queryKey: ["client-devices", id], queryFn: () => clientsApi.devices(id), enabled: !!id });
  const gateways = useQuery({ queryKey: ["client-gateways", id], queryFn: () => clientsApi.gateways(id), enabled: !!id });
  const contacts = useQuery({ queryKey: ["contacts"], queryFn: () => contactsApi.list() });

  if (clients.isLoading) {
    return <PageContainer><p className="text-sm text-zinc-500">Loading…</p></PageContainer>;
  }
  if (!client) {
    return (
      <PageContainer>
        <p className="text-sm text-zinc-500">Client not found.</p>
      </PageContainer>
    );
  }

  const primary = contacts.data?.find((c) => c.id === client.primary_contact_id);
  const cityLine = [client.zipcode, client.city].filter(Boolean).join(" ");

  return (
    <PageContainer>
      <button
        onClick={() => navigate("/clients")}
        className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3"
        data-testid="client-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Clients
      </button>

      <PageHeader
        title={client.name}
        subtitle="Client profile · address, primary contact and infrastructure summary."
        actions={
          <button
            data-testid="client-edit-open"
            onClick={() => setEditing(true)}
            className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
        }
      />

      {/* Address + Contact blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="client-address-card">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">
            <MapPin className="w-3.5 h-3.5" /> Address
          </div>
          <div className="text-sm leading-relaxed">
            {client.address_line1 || client.address_line2 || cityLine || client.country ? (
              <>
                {client.address_line1 && <div>{client.address_line1}</div>}
                {client.address_line2 && <div>{client.address_line2}</div>}
                {cityLine && <div>{cityLine}</div>}
                {client.country && <div>{client.country}</div>}
              </>
            ) : (
              <div className="text-zinc-400 italic">No address on file.</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="client-contact-card">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">
            <Mail className="w-3.5 h-3.5" /> Primary contact
          </div>
          {primary ? (
            <div className="text-sm space-y-1.5">
              <div className="font-medium text-base">{primary.name}</div>
              {primary.role && (
                <div className="text-[10px] uppercase tracking-widest font-mono bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded inline-block">
                  {primary.role}
                </div>
              )}
              {primary.email && (
                <div className="text-zinc-600 inline-flex items-center gap-1.5 mt-1">
                  <Mail className="w-3.5 h-3.5 text-zinc-400" />
                  <a href={`mailto:${primary.email}`} className="hover:text-zinc-900 font-mono text-xs">
                    {primary.email}
                  </a>
                </div>
              )}
              {primary.phone && (
                <div className="text-zinc-600 inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-zinc-400" />
                  <a href={`tel:${primary.phone}`} className="hover:text-zinc-900 font-mono text-xs">
                    {primary.phone}
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-zinc-400 italic">
              No primary contact assigned.{" "}
              <Link to="/contacts" className="text-zinc-700 underline hover:text-zinc-900">Manage contacts</Link>.
            </div>
          )}
        </div>
      </div>

      {/* Devices + Gateways summary cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Link
          to={`/devices?client=${client.id}`}
          className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-900 hover:shadow-sm transition group"
          data-testid="client-devices-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <Cpu className="w-3.5 h-3.5" /> Devices
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 transition" />
          </div>
          <div className="mt-3 font-heading text-3xl font-bold tracking-tight">
            {devices.data?.length ?? "—"}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-400 font-mono group-hover:text-zinc-900 transition">
            view all →
          </div>
        </Link>

        <Link
          to={`/gateways?client=${client.id}`}
          className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-900 hover:shadow-sm transition group"
          data-testid="client-gateways-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <Radio className="w-3.5 h-3.5" /> Gateways
            </div>
            <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 transition" />
          </div>
          <div className="mt-3 font-heading text-3xl font-bold tracking-tight">
            {gateways.data?.length ?? "—"}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-400 font-mono group-hover:text-zinc-900 transition">
            view all →
          </div>
        </Link>
      </div>

      {editing && (
        <EditClientModal
          client={client}
          contacts={contacts.data || []}
          onClose={() => setEditing(false)}
        />
      )}
    </PageContainer>
  );
}
